import { useEffect, useRef, useState, type ReactNode } from 'react';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack, Typography } from '@mui/material';

import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';

// A screen frame that can be scaled, so an exploration can be read rather than
// just glanced at. Deliberately separate from handover/MiniScreen: that one is
// fixed at thumbnail scale because a handover is a reference document, whereas
// an exploration gets walked through in a room and has to be legible on a
// shared screen. Sizing MiniScreen instead would have changed every handover.

export type FrameSize = 'compact' | 'large' | 'full';

export type ScreenVariant = 'phone' | 'desktop';

const DIMS: Record<ScreenVariant, { width: number; height: number }> = {
  phone: { width: 375, height: 812 },
  desktop: { width: 1440, height: 900 },
};

// Desktop frames render a full admin page, so they sit at a lower scale than
// phones at every size to land at a comparable footprint on the page.
export const SCALE: Record<
  FrameSize,
  { phone: number; desktop: number; bare: number }
> = {
  compact: { phone: 0.5, desktop: 0.3, bare: 620 },
  large: { phone: 0.75, desktop: 0.45, bare: 820 },
  full: { phone: 1, desktop: 0.6, bare: 1040 },
};

export const SIZE_LABEL: Record<FrameSize, string> = {
  compact: 'Compact',
  large: 'Large',
  full: 'Full size',
};

// One step smaller, for the at-a-glance strip at the top of a sequence — it
// still responds to the control, but a six-frame journey stays scannable
// instead of turning into two metres of horizontal scroll.
export const oneStepDown: Record<FrameSize, FrameSize> = {
  compact: 'compact',
  large: 'compact',
  full: 'large',
};

// Mounts its children only while they are near the viewport.
//
// An exploration holds every frame it has — Tell Us Once is 34 complete phone
// screens, each a full React tree, plus PDF viewers. Mounted all at once that
// is gigabytes of renderer memory for a page you read a screenful at a time.
// The frame's own box is always laid out, so nothing reflows as content comes
// and goes; only the expensive interior is conditional.
//
// `keepMounted` is for frames holding state a reader would be annoyed to lose
// — a carousel that has been paged forward should still be there when they
// scroll back to it.
export const LazyMount = ({
  keepMounted = false,
  children,
}: {
  keepMounted?: boolean;
  children: ReactNode;
}) => {
  const [near, setNear] = useState(false);
  const [everNear, setEverNear] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setNear(entry.isIntersecting);
        if (entry.isIntersecting) setEverNear(true);
      },
      // A screenful of runway either side, so content is ready before it is
      // scrolled to and blank frames are never seen.
      { rootMargin: '800px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Box sx={{ width: '100%', height: '100%' }} ref={ref}>
      {(keepMounted ? everNear : near) ? children : null}
    </Box>
  );
};

const ExploreScreen = ({
  label,
  caption,
  variant = 'phone',
  size,
  // Rendered over the screen itself, not the caption beneath it — so a hover
  // state can cover the sketch without hiding which frame it belongs to.
  overlay,
  keepMounted,
  children,
}: {
  label: string;
  caption?: string;
  variant?: ScreenVariant;
  size: FrameSize;
  overlay?: ReactNode;
  /** Keep the interior mounted once seen — for frames holding state. */
  keepMounted?: boolean;
  children: ReactNode;
}) => {
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);
  const { width, height } = DIMS[variant];
  const scale = SCALE[size][variant];

  return (
    <Stack sx={{ width: width * scale, flexShrink: 0 }} spacing={1}>
      <Box
        sx={{
          position: 'relative',
          width: width * scale,
          height: height * scale,
          border: 1,
          borderColor: 'custom.neutral.200',
          // Scaled with the frame rather than fixed. The interior is
          // transformed to `scale`, but a radius is not, so a constant 12px
          // corner reads three times as round at compact desktop (0.3) as it
          // does at full size. sx is correct here: this is Loft chrome, and
          // the value is a function of a runtime prop, so no theme token can
          // express it.
          borderRadius: `${radius.xl * scale}px`,
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        <LazyMount keepMounted={keepMounted}>
          <Box
            ref={setScreenEl}
            sx={{
              position: 'relative',
              width: width,
              height: height,
              overflow: 'hidden',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {/* Bottom sheets and pushed pages portal inside the frame rather
                than escaping it. */}
            <PhoneScreenContext.Provider value={screenEl}>
              {children}
            </PhoneScreenContext.Provider>
          </Box>
        </LazyMount>
        {overlay}
      </Box>
      <Box>
        <Typography variant='subtitle2'>{label}</Typography>
        {caption && (
          <Typography
            sx={{ display: 'block', mt: 0.25 }}
            variant='caption'
            color='text.secondary'
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

ExploreScreen.displayName = 'ExploreScreen';
export default ExploreScreen;
